import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { CanvasSession } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { canvasKeys, getSession } from '../api/canvas-captures';
import {
  getRoomForCapture,
  getRoomForSession,
  roomKeys,
} from '../api/research-rooms';
import { FixListButton, useFixList } from '../fix-list/fix-list';
import { roomFixes } from '../fix-list/room-fix-list';
import { useIsMobile } from '../lib/use-is-mobile';
import { usePollWindow } from '../lib/use-poll-window';
import { usePersonas } from '../persona/use-personas';
import { ResearchRoomPanel } from '../research-room/research-room-panel';
import { RoomIntroModal } from '../research-room/room-intro-modal';
import { useResearchRoomStore } from '../store/research-room-store';
import { CanvasBoard } from './canvas-board';
import { MobileSession } from './mobile-session';
import { useCaptures } from './use-captures';

/**
 * A session's board — the screens a walk took, with no browser.
 *
 * Opening old work must not cost a worker instance: at --concurrency=1 that is
 * a whole billed CPU rendering a page nobody asked to see.
 */
export function SessionPage() {
  const { sessionId = '' } = useParams();
  // The router keeps this component mounted when only `:sessionId` changes, so
  // local state (the selected capture, the "open the first capture" guard)
  // would carry over from the session just left. Keying on `sessionId` forces
  // a fresh instance per session, exactly like a full page load gives for free.
  return <SessionBoard key={sessionId} sessionId={sessionId} />;
}

function SessionBoard({ sessionId }: { sessionId: string }) {
  const navigate = useNavigate();
  // Arriving from the walk that made this board, which was already showing
  // it — so no entrance, or the handover blinks.
  const location = useLocation();
  const [fromWalk] = useState(
    () => (location.state as { fromWalk?: boolean } | null)?.fromWalk === true,
  );
  /** Where the click that brought them here was — a room card, the
   *  Continue card, a report's way back. Absent for a typed or shared link. */
  const [arrivedFrom] = useState(
    () => (location.state as { from?: string } | null)?.from ?? 'link',
  );
  /** The extension drops people straight into a room — from an audit, or a
   *  walk it started — so it says what a room is first. History state
   *  outlives a reload, so closing it clears the flag rather than only
   *  hiding it. */
  const [introOpen, setIntroOpen] = useState(
    () =>
      (location.state as { intro?: boolean } | null)?.intro !== false &&
      (arrivedFrom === 'extension-audit' || arrivedFrom === 'extension'),
  );
  const closeIntro = useCallback(() => {
    setIntroOpen(false);
    void navigate(location.pathname, {
      replace: true,
      state: { from: arrivedFrom, intro: false },
    });
  }, [arrivedFrom, location.pathname, navigate]);

  // The session is named a few seconds after its first capture — see
  // useCaptures. Read from the cache and bounded, for the same reasons.
  const queryClient = useQueryClient();
  const cachedSession = queryClient.getQueryData<CanvasSession>(
    canvasKeys.session(sessionId),
  );
  const namePoll = usePollWindow(Boolean(cachedSession && !cachedSession.name));
  const sessionQuery = useQuery({
    queryFn: () => getSession(sessionId),
    queryKey: canvasKeys.session(sessionId),
    refetchInterval: namePoll.refetchInterval,
  });
  const session = sessionQuery.data;

  const {
    captures,
    loading: capturesLoading,
    moveCard,
  } = useCaptures(sessionId);

  const [selectedCapture, setSelectedCapture] = useState<string | null>(null);
  const roomOpen = useResearchRoomStore((s) => s.open);
  const spotlight = useResearchRoomStore((s) => s.spotlight);
  const heatmap = useResearchRoomStore((s) => s.heatmap);
  const clearSpotlight = useResearchRoomStore((s) => s.clearSpotlight);

  // The fix list lives on the board, beside the selected screen. This keeps
  // the project's list live for it, and the bar's pill takes the board there
  // rather than opening a list of its own.
  const { entries: fixListEntries } = useFixList(session?.projectId);

  // The chat belongs to its context — the screen it began on — not to the
  // board's selection: selecting another screen, or none, leaves it showing.
  // Before any room exists, the selected screen is where one would open.
  // A re-test, or going back to the room it came from, picks the chat
  // explicitly (`chatCapture`).
  const { data: sessionRoom, isPending: sessionRoomPending } = useQuery({
    queryFn: () => getRoomForSession(sessionId),
    queryKey: roomKeys.forSession(sessionId),
  });
  const [chatCapture, setChatCapture] = useState<string | null>(null);
  const contextCapture =
    chatCapture ?? sessionRoom?.room.context.captureIds[0] ?? selectedCapture;
  // The chat's room, as the panel keeps it — live through a round.
  const { data: chatRoom } = useQuery({
    enabled: Boolean(contextCapture),
    queryFn: () => getRoomForCapture(contextCapture ?? ''),
    queryKey: roomKeys.forCapture(contextCapture ?? 'none'),
  });
  const fixListOpenCount = chatRoom
    ? roomFixes(fixListEntries, chatRoom.room.id).filter(
        (entry) => entry.status === 'fix',
      ).length
    : 0;
  const setRoomOpen = useResearchRoomStore((s) => s.setOpen);
  const viewFixList = useResearchRoomStore((s) => s.viewAnalysis);

  // Opening a board selects its first screen. Guarded by a ref so it is an
  // opening move, not a rule.
  const openedRef = useRef(false);
  useEffect(() => {
    const first = captures[0];
    if (openedRef.current || !first) return;
    openedRef.current = true;
    setSelectedCapture(first.id);
    // The walk counted its own arrival (`source: 'instant'`) as it handed
    // over; everyone else coming back to a board is counted here.
    if (!fromWalk) {
      trackEvent('board_opened', {
        captures: captures.length,
        session_id: sessionId,
        source: arrivedFrom,
      });
    }
  }, [arrivedFrom, captures, fromWalk, sessionId]);

  const mobile = useIsMobile();
  // Who the room seats, for the phone layout's Personas tab: the room's own
  // roster once it exists, else the active panel — as the room panel does.
  const { personas: panel } = usePersonas({ projectId: session?.projectId });
  const exit = () => {
    trackEvent('board_exited', {
      captures: captures.length,
      room_open: roomOpen,
    });
    void navigate('/');
  };
  const chatCaptureObject =
    captures.find((capture) => capture.id === contextCapture) ?? null;

  if (mobile) {
    const roster = chatRoom
      ? chatRoom.room.personaIds.flatMap(
          (id) => panel.find((persona) => persona.id === id) ?? [],
        )
      : panel.filter((persona) => persona.active).slice(0, 5);
    return (
      <MobileSession
        captures={captures}
        contextCapture={chatCaptureObject}
        onExit={exit}
        personas={roster}
        room={
          <ResearchRoomPanel
            capture={chatCaptureObject}
            captures={captures}
            enter={false}
            hasCaptures={captures.length > 0}
            mobile
            onSelectCapture={(captureId) => {
              setChatCapture(captureId);
              setSelectedCapture(captureId);
            }}
            pending={
              sessionQuery.isPending || capturesLoading || sessionRoomPending
            }
            projectId={session?.projectId}
          />
        }
        roomReady={Boolean(chatRoom)}
      />
    );
  }

  return (
    <>
      <div className="flex h-svh">
        <CanvasBoard
          captures={captures}
          enter={!fromWalk}
          readTop={fromWalk}
          exitLabel="All rooms"
          onExit={exit}
          onMove={moveCard}
          onClearSpotlight={clearSpotlight}
          onSelect={setSelectedCapture}
          selectedId={selectedCapture}
          heatmap={roomOpen && heatmap}
          fixList={roomOpen}
          loading={capturesLoading}
          titlePending={sessionQuery.isPending}
          // On the board, not beside it: the board frames its screens in the
          // part the room leaves clear and keeps panning while the room talks.
          panel={
            roomOpen ? (
              <ResearchRoomPanel
                capture={chatCaptureObject}
                enter={!fromWalk}
                captures={captures}
                hasCaptures={captures.length > 0}
                onSelectCapture={(captureId) => {
                  setChatCapture(captureId);
                  setSelectedCapture(captureId);
                }}
                pending={
                  sessionQuery.isPending ||
                  capturesLoading ||
                  sessionRoomPending
                }
                projectId={session?.projectId}
              />
            ) : null
          }
          spotlight={spotlight}
          title={session?.name ?? session?.label ?? 'Room'}
          // The balance lives under the room's composer, where it is spent;
          // the corner keeps only what acts on the board.
          topRight={
            <div className="flex items-center gap-2 *:shadow-popover">
              <FixListButton
                count={fixListOpenCount}
                onClick={() => {
                  if (!contextCapture) return;
                  trackEvent('fix_list_opened', {
                    open: fixListOpenCount,
                    source: 'topbar',
                  });
                  // The card shows only with the room open.
                  setRoomOpen(true);
                  viewFixList(contextCapture, 'show');
                }}
              />
            </div>
          }
        />
      </div>
      {introOpen ? <RoomIntroModal onClose={closeIntro} /> : null}
    </>
  );
}
